import {
  AttachMoney,
  FilterAltOff,
  Gavel,
  LocalOffer,
  Paid,
  RequestQuote,
  Search,
  Speed,
  Store,
  Storefront,
  Timer,
  TimerOff
} from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  Fade,
  LinearProgress,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  Tooltip,
} from "chart.js";
import { useCallback, useEffect, useRef, useState } from "react";
import { Bar, Doughnut } from "react-chartjs-2";
import styled, { keyframes } from "styled-components";
import { useToastStore } from "../../../stores";
import { colorPrimary } from "../../../theme";
import { getFirstDayOfMonth } from "../../../utils/methods/inititalDateTimeFilter";
import {
  fetchGrupoConcessionaria,
  fetchPagamentoAnalitico,
} from "../repository/AdminRepository";
import {
  GrupoConcessionaria,
  PagamentoAnaliticoData,
  ParametrosGrupoConcessionaria,
} from "../types/types";

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Filler, Tooltip, Legend);

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const AnimatedBox = styled.div<{ $delay?: number }>`
  animation: ${slideUp} 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  animation-delay: ${({ $delay }) => $delay ?? 0}ms;
  opacity: 0;
`;

const CHART_PALETTE = [
  colorPrimary, "#10b981", "#0284c7", "#8b5cf6", "#ef4444",
  "#f59e0b", "#ec4899", "#14b8a6", "#6366f1", "#78716c",
  "#d946ef", "#06b6d4", "#84cc16", "#f97316", "#a855f7",
  "#22d3ee", "#fb923c", "#a3e635", "#e879f9", "#34d399",
];

const MONTH_NAMES = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];

function formatCurrency(val: number): string {
  return val.toLocaleString("pt-BR", { style: "currency", currency: "BRL", minimumFractionDigits: 0 });
}

function formatNumber(val: number | null): string {
  if (val == null) return "0";
  return val.toLocaleString("pt-BR");
}

function formatMinutesToReadable(minutes: number): string {
  if (minutes < 60) return `${minutes}min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h < 24) return `${h}h ${m > 0 ? m + "min" : ""}`.trim();
  const d = Math.floor(h / 24);
  const rh = h % 24;
  return `${d}d ${rh > 0 ? rh + "h" : ""}`.trim();
}

interface AutocompleteOption {
  id: string;
  label: string;
}

const TODAS_CONCESSIONARIAS: AutocompleteOption = { id: "todos", label: "Todas as Concessionárias" };

function toOptions(items: ParametrosGrupoConcessionaria[]): AutocompleteOption[] {
  return items.map((i) => ({ id: String(i.id), label: i.label }));
}

const autocompleteSx = {
  "& .MuiOutlinedInput-root": {
    bgcolor: "white",
    borderRadius: 2,
    fontSize: "0.85rem",
    "& fieldset": { border: "1px solid #e2e8f0" },
    "&:hover fieldset": { borderColor: colorPrimary + "80" },
    "&.Mui-focused fieldset": { borderColor: colorPrimary },
  },
  "& .MuiAutocomplete-popupIndicator": { color: "#94a3b8" },
  "& .MuiAutocomplete-clearIndicator": { color: "#94a3b8" },
} as const;

const inputSx = {
  "& .MuiOutlinedInput-root": {
    bgcolor: "white",
    borderRadius: 2,
    fontSize: "0.85rem",
    "& fieldset": { border: "1px solid #e2e8f0" },
    "&:hover fieldset": { borderColor: colorPrimary + "80" },
    "&.Mui-focused fieldset": { borderColor: colorPrimary },
  },
} as const;

const labelSx = {
  component: "label" as const,
  fontSize: "0.7rem",
  fontWeight: 700,
  textTransform: "uppercase" as const,
  letterSpacing: 1,
  color: "#64748b",
  mb: 0.5,
  display: "block",
};

interface KpiCardProps {
  label: string;
  value: string;
  subtitle: string;
  icon: React.ReactNode;
  accentColor: string;
  bgColor: string;
  delay: number;
}

function KpiCard({ label, value, subtitle, icon, accentColor, bgColor, delay }: KpiCardProps) {
  return (
    <AnimatedBox $delay={delay}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 3,
          position: "relative",
          overflow: "hidden",
          border: "1px solid #f1f5f9",
          height: "100%",
          transition: "all 0.3s cubic-bezier(0.16, 1, 0.3, 1)",
          "&:hover": {
            transform: "translateY(-4px)",
            boxShadow: "0 12px 40px rgba(0,0,0,0.08)",
            borderColor: accentColor + "40",
          },
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Typography
            fontSize="0.7rem"
            fontWeight={700}
            textTransform="uppercase"
            letterSpacing={1.5}
            color="#94a3b8"
          >
            {label}
          </Typography>
          <Box
            sx={{
              bgcolor: bgColor,
              color: accentColor,
              p: 1,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {icon}
          </Box>
        </Box>
        <Box display="flex" alignItems="baseline" gap={1}>
          <Typography
            fontSize="2rem"
            fontWeight={900}
            letterSpacing={-1.5}
            color={accentColor}
            lineHeight={1}
          >
            {value}
          </Typography>
          <Typography fontSize="0.7rem" fontWeight={600} color={accentColor + "99"}>
            {subtitle}
          </Typography>
        </Box>
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            width: "100%",
            height: 3,
            bgcolor: accentColor + "30",
          }}
        />
      </Paper>
    </AnimatedBox>
  );
}

function KpiCardSkeleton({ delay }: { delay: number }) {
  return (
    <AnimatedBox $delay={delay}>
      <Paper
        elevation={0}
        sx={{ p: 3, borderRadius: 3, border: "1px solid #f1f5f9", overflow: "hidden", position: "relative" }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Skeleton variant="text" width={100} height={16} sx={{ borderRadius: 1 }} />
          <Skeleton variant="rounded" width={36} height={36} sx={{ borderRadius: 2 }} />
        </Box>
        <Box display="flex" alignItems="baseline" gap={1}>
          <Skeleton variant="text" width={80} height={40} sx={{ borderRadius: 1 }} />
          <Skeleton variant="text" width={60} height={14} sx={{ borderRadius: 1 }} />
        </Box>
        <Box sx={{ position: "absolute", bottom: 0, left: 0, width: "100%", height: 3, bgcolor: "#e2e8f0" }} />
      </Paper>
    </AnimatedBox>
  );
}

function ChartSkeleton() {
  return (
    <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: "1px solid #f1f5f9" }}>
      <Skeleton variant="text" width={200} height={24} sx={{ mb: 2, borderRadius: 1 }} />
      <Skeleton variant="rounded" width="100%" height={280} sx={{ borderRadius: 2 }} />
    </Paper>
  );
}

function TableSkeleton() {
  return (
    <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: "1px solid #f1f5f9" }}>
      <Skeleton variant="text" width={180} height={24} sx={{ mb: 2, borderRadius: 1 }} />
      {Array.from({ length: 5 }).map((_, i) => (
        <Box key={i} display="flex" alignItems="center" gap={2} py={1.5}>
          <Skeleton variant="text" width="60%" height={18} sx={{ borderRadius: 1 }} />
          <Skeleton variant="text" width={60} height={18} sx={{ borderRadius: 1, ml: "auto" }} />
        </Box>
      ))}
    </Paper>
  );
}

const thSx = {
  fontSize: "0.68rem",
  fontWeight: 700,
  textTransform: "uppercase" as const,
  letterSpacing: 1.5,
  color: "#94a3b8",
  borderBottom: "2px solid #f1f5f9",
  py: 1.5,
  whiteSpace: "nowrap" as const,
};

const doughnutOptions = {
  responsive: true,
  maintainAspectRatio: false,
  cutout: "65%",
  plugins: {
    legend: {
      position: "bottom" as const,
      labels: {
        usePointStyle: true,
        padding: 16,
        font: { size: 11, weight: 600 as const },
        color: "#475569",
      },
    },
    tooltip: {
      backgroundColor: "#1e293b",
      titleFont: { size: 13, weight: 700 as const },
      bodyFont: { size: 12 },
      padding: 12,
      cornerRadius: 8,
      callbacks: {
        label: (ctx: any) => {
          const total = ctx.dataset.data.reduce((a: number, b: number) => a + b, 0);
          const pct = total > 0 ? ((ctx.parsed / total) * 100).toFixed(1) : "0";
          return ` ${ctx.label}: ${formatCurrency(ctx.parsed)} (${pct}%)`;
        },
      },
    },
  },
};

export function PagamentoAnaliticoTab() {
  const [dataInicio, setDataInicio] = useState(getFirstDayOfMonth());
  const [dataFim, setDataFim] = useState(new Date().toISOString().split("T")[0] || "");
  const [concessionaria, setConcessionaria] = useState<AutocompleteOption>(TODAS_CONCESSIONARIAS);
  const [parametros, setParametros] = useState<GrupoConcessionaria | null>(null);
  const [dashboard, setDashboard] = useState<PagamentoAnaliticoData | null>(null);
  const [loading, setLoading] = useState(true);
  const { setToast } = useToastStore();
  const jaCarregouRef = useRef(false);
  const fetchedParamsRef = useRef(false);

  useEffect(() => {
    if (fetchedParamsRef.current) return;
    fetchedParamsRef.current = true;
    (async () => {
      try {
        const result = await fetchGrupoConcessionaria();
        if (result?.success) setParametros(result.data);
      } catch { /* ignore */ }
    })();
  }, []);

  const concessionariaOptions: AutocompleteOption[] = [
    TODAS_CONCESSIONARIAS,
    ...toOptions(parametros?.concessionarias ?? []),
  ];

  const fetchDash = useCallback(async () => {
    try {
      setLoading(true);
      const idConc = concessionaria.id === "todos" ? null : concessionaria.id;
      const result = await fetchPagamentoAnalitico(idConc, dataInicio, dataFim);
      if (!result?.success) {
        setToast({ variant: "error", message: result.message });
        return;
      }
      setDashboard(result.data);
    } catch {
      setToast({ variant: "error", message: "Erro. Contate o administrador." });
    } finally {
      setLoading(false);
    }
  }, [dataInicio, dataFim, concessionaria, setToast]);

  useEffect(() => {
    if (jaCarregouRef.current) return;
    jaCarregouRef.current = true;
    fetchDash();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetFilters = useCallback(async () => {
    setConcessionaria(TODAS_CONCESSIONARIAS);
    setDataInicio(getFirstDayOfMonth());
    setDataFim(new Date().toISOString().split("T")[0]);
    await fetchDash();
  }, []);

  const graficoMensal = dashboard?.grafico_mensal ?? [];
  const topLojistas = dashboard?.top_lojistas_receita ?? [];
  const topAnunciantes = dashboard?.top_anunciantes_receita ?? [];

  const totalRecebido =
    (dashboard?.valor_recebido_normais ?? 0) +
    (dashboard?.valor_recebido_leilao ?? 0) +
    (dashboard?.valor_recebido_cotacao ?? 0) +
    (dashboard?.valor_recebido_combo ?? 0);

  // --- Monthly chart ---
  const monthlyLabels = graficoMensal.map((g) => `${MONTH_NAMES[g.mes - 1]}/${g.ano}`);
  const monthlyChartData = {
    labels: monthlyLabels,
    datasets: [
      {
        label: "Valor Cobrado",
        data: graficoMensal.map((g) => g.valor_cobrado),
        backgroundColor: "#0284c7cc",
        hoverBackgroundColor: "#0284c7",
        borderRadius: 6,
        maxBarThickness: 40,
        yAxisID: "y",
      },
      {
        label: "Valor Recebido (Taxa Canal)",
        data: graficoMensal.map((g) => g.valor_recebido_taxa_canal),
        backgroundColor: colorPrimary + "cc",
        hoverBackgroundColor: colorPrimary,
        borderRadius: 6,
        maxBarThickness: 40,
        yAxisID: "y",
      },
      {
        label: "Qtd Vendido",
        data: graficoMensal.map((g) => g.qtd_vendido),
        backgroundColor: "#10b981cc",
        hoverBackgroundColor: "#10b981",
        borderRadius: 6,
        maxBarThickness: 40,
        yAxisID: "y1",
      },
    ],
  };

  const monthlyChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top" as const,
        labels: { usePointStyle: true, padding: 20, font: { size: 12, weight: 600 as const } },
      },
      tooltip: {
        backgroundColor: "#1e293b",
        titleFont: { size: 13, weight: 700 as const },
        bodyFont: { size: 12 },
        padding: 12,
        cornerRadius: 8,
        callbacks: {
          label: (ctx: any) => {
            if (ctx.datasetIndex === 2) return ` ${ctx.dataset.label}: ${ctx.parsed.y}`;
            return ` ${ctx.dataset.label}: ${formatCurrency(ctx.parsed.y)}`;
          },
        },
      },
    },
    scales: {
      x: { grid: { display: false }, ticks: { font: { size: 11, weight: 600 as const }, color: "#475569" } },
      y: {
        position: "left" as const,
        grid: { color: "#f1f5f9" },
        ticks: {
          font: { size: 10 },
          color: "#94a3b8",
          callback: (val: any) => formatCurrency(Number(val)),
        },
      },
      y1: {
        position: "right" as const,
        grid: { display: false },
        ticks: { font: { size: 10 }, color: "#10b981" },
      },
    },
  };

  // --- Doughnut: receita por tipo ---
  const receitaPorTipoData = {
    labels: ["COMPRE AGORA", "Leilão", "Cotação", "Combo"],
    datasets: [{
      data: [
        dashboard?.valor_recebido_normais ?? 0,
        dashboard?.valor_recebido_leilao ?? 0,
        dashboard?.valor_recebido_cotacao ?? 0,
        dashboard?.valor_recebido_combo ?? 0,
      ],
      backgroundColor: [colorPrimary, "#0284c7", "#8b5cf6", "#10b981"],
      hoverBackgroundColor: [colorPrimary + "cc", "#0284c7cc", "#8b5cf6cc", "#10b981cc"],
      borderWidth: 0,
    }],
  };

  // --- Top Lojistas bar chart ---
  const topLojistasChartData = {
    labels: topLojistas.map((l) => l.revenda_label),
    datasets: [
      {
        label: "Receita Taxa Canal (R$)",
        data: topLojistas.map((l) => l.valor_recebido_taxa_canal),
        backgroundColor: colorPrimary + "cc",
        hoverBackgroundColor: colorPrimary,
        borderRadius: 6,
        maxBarThickness: 32,
        yAxisID: "y",
      },
      {
        label: "Qtd Comprados",
        data: topLojistas.map((l) => l.qtd_anuncio_comprado),
        backgroundColor: "#0284c7cc",
        hoverBackgroundColor: "#0284c7",
        borderRadius: 6,
        maxBarThickness: 32,
        yAxisID: "y1",
      },
    ],
  };

  const horizontalBarOptions = (currencyAxisId: string) => ({
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: "y" as const,
    plugins: {
      legend: {
        position: "top" as const,
        labels: { usePointStyle: true, padding: 20, font: { size: 12, weight: 600 as const } },
      },
      tooltip: {
        backgroundColor: "#1e293b",
        titleFont: { size: 13, weight: 700 as const },
        bodyFont: { size: 12 },
        padding: 12,
        cornerRadius: 8,
        callbacks: {
          label: (ctx: any) => {
            if (ctx.dataset.yAxisID === currencyAxisId || ctx.datasetIndex === 0)
              return ` ${ctx.dataset.label}: ${formatCurrency(ctx.parsed.x)}`;
            return ` ${ctx.dataset.label}: ${ctx.parsed.x}`;
          },
        },
      },
    },
    scales: {
      y: {
        grid: { display: false },
        ticks: { font: { size: 11, weight: 600 as const }, color: "#475569" },
      },
      x: {
        position: "top" as const,
        grid: { color: "#f1f5f9" },
        ticks: {
          font: { size: 10 },
          color: "#94a3b8",
          callback: (val: any) => formatCurrency(Number(val)),
        },
      },
      y1: { display: false },
    },
  });

  // --- Top Anunciantes bar chart ---
  const topAnunciantesChartData = {
    labels: topAnunciantes.map((a) => a.concessionaria_label),
    datasets: [
      {
        label: "Receita Taxa Canal (R$)",
        data: topAnunciantes.map((a) => a.valor_recebido_taxa_canal),
        backgroundColor: "#8b5cf6cc",
        hoverBackgroundColor: "#8b5cf6",
        borderRadius: 6,
        maxBarThickness: 32,
        yAxisID: "y",
      },
      {
        label: "Qtd Vendidos",
        data: topAnunciantes.map((a) => a.qtd_anuncio_vendido),
        backgroundColor: "#10b981cc",
        hoverBackgroundColor: "#10b981",
        borderRadius: 6,
        maxBarThickness: 32,
        yAxisID: "y1",
      },
    ],
  };

  const topLojistasChartHeight = Math.max(320, topLojistas.length * 48);
  const topAnunciantesChartHeight = Math.max(320, topAnunciantes.length * 48);

  return (
    <Fade in timeout={400}>
      <Box display="flex" flexDirection="column" gap={3}>
        {/* FILTROS */}
        <AnimatedBox $delay={0}>
          <Paper elevation={0} sx={{ p: 3.5, borderRadius: 3, border: "1px solid #f1f5f9" }}>
            <Typography
              fontSize="0.7rem"
              fontWeight={700}
              textTransform="uppercase"
              letterSpacing={1.5}
              color="#94a3b8"
              mb={2.5}
            >
              Filtros Analítico Pagamentos
            </Typography>
            <Box
              display="grid"
              gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr", lg: "1fr 1fr 0.7fr 0.5fr" }}
              gap={2}
              alignItems="end"
            >
              <Box>
                <Typography {...labelSx}>Concessionária</Typography>
                <Autocomplete
                  size="small"
                  options={concessionariaOptions}
                  value={concessionaria}
                  onChange={(_, opt) => setConcessionaria(opt ?? TODAS_CONCESSIONARIAS)}
                  getOptionLabel={(o) => o.label}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  disableClearable
                  noOptionsText="Nenhuma concessionária encontrada"
                  sx={autocompleteSx}
                  renderInput={(params) => (
                    <TextField {...params} placeholder="Buscar concessionária..." />
                  )}
                />
              </Box>
              <Box>
                <Typography {...labelSx}>Período</Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    value={dataInicio}
                    onChange={(e) => setDataInicio(e.target.value)}
                    sx={inputSx}
                  />
                  <Typography fontSize="0.75rem" color="#94a3b8" flexShrink={0}>
                    até
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    value={dataFim}
                    onChange={(e) => setDataFim(e.target.value)}
                    sx={inputSx}
                  />
                </Box>
              </Box>
              <Button
                variant="outlined"
                startIcon={<Search sx={{ fontSize: "17px !important" }} />}
                disableElevation
                disabled={loading}
                onClick={fetchDash}
                sx={{
                  color: colorPrimary,
                  borderColor: "#e2e8f0",
                  fontWeight: 600,
                  fontSize: "0.85rem",
                  textTransform: "none",
                  borderRadius: 2,
                  px: 3,
                  height: 40,
                  whiteSpace: "nowrap",
                  bgcolor: "white",
                  "&:hover": { borderColor: colorPrimary, bgcolor: colorPrimary + "08" },
                  transition: "all 0.2s",
                }}
              >
                Gerar
              </Button>
              <Button
              variant="text"
              startIcon={<FilterAltOff sx={{ fontSize: "17px !important" }} />}
              disabled={loading}
              onClick={resetFilters}
              sx={{
                color: "#94a3b8",
                fontWeight: 600,
                fontSize: "0.85rem",
                textTransform: "none",
                borderRadius: 2,
                px: 2,
                height: 40,
                whiteSpace: "nowrap",
                "&:hover": {
                  color: colorPrimary,
                  bgcolor: colorPrimary + "08",
                },
                transition: "all 0.2s",
              }}
            >
              Limpar
            </Button>
            </Box>
          </Paper>
        </AnimatedBox>

        {/* KPI ROW 1 - Tempos de Pagamento */}
        <Box
          display="grid"
          gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr 1fr" }}
          gap={2.5}
        >
          {loading ? (
            <>
              <KpiCardSkeleton delay={80} />
              <KpiCardSkeleton delay={160} />
              <KpiCardSkeleton delay={240} />
            </>
          ) : (
            <>
              <KpiCard
                label="Tempo Mín Pagamento"
                value={formatMinutesToReadable(dashboard?.tempo_min_pagamento_minute ?? 0)}
                subtitle="mais rápido"
                icon={<Timer />}
                accentColor="#10b981"
                bgColor="#ecfdf5"
                delay={80}
              />
              <KpiCard
                label="Tempo Médio Pagamento"
                value={formatMinutesToReadable(dashboard?.tempo_medio_pagamento_minute ?? 0)}
                subtitle="em média"
                icon={<Speed />}
                accentColor="#0284c7"
                bgColor="#e0f2fe"
                delay={160}
              />
              <KpiCard
                label="Tempo Máx Pagamento"
                value={formatMinutesToReadable(dashboard?.tempo_max_pagamento_minute ?? 0)}
                subtitle="mais lento"
                icon={<TimerOff />}
                accentColor="#ef4444"
                bgColor="#fef2f2"
                delay={240}
              />
            </>
          )}
        </Box>

        {/* KPI ROW 2 - Receita por tipo + Total */}
        <Box
          display="grid"
          gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr", lg: "repeat(4, 1fr)" }}
          gap={2.5}
        >
          {loading ? (
            <>
              <KpiCardSkeleton delay={300} />
              <KpiCardSkeleton delay={360} />
              <KpiCardSkeleton delay={420} />
              <KpiCardSkeleton delay={480} />
            </>
          ) : (
            <>
              <KpiCard
                label="COMPRE AGORA"
                value={formatCurrency(dashboard?.valor_recebido_normais ?? 0)}
                subtitle={`${formatNumber(dashboard?.qtd_anuncio_normais ?? 0)} anúncios`}
                icon={<LocalOffer />}
                accentColor={colorPrimary}
                bgColor="#fff7ed"
                delay={300}
              />
              <KpiCard
                label="Leilão"
                value={formatCurrency(dashboard?.valor_recebido_leilao ?? 0)}
                subtitle={`${formatNumber(dashboard?.qtd_anuncio_leilao ?? 0)} anúncios`}
                icon={<Gavel />}
                accentColor="#0284c7"
                bgColor="#e0f2fe"
                delay={360}
              />
              <KpiCard
                label="Cotação"
                value={formatCurrency(dashboard?.valor_recebido_cotacao ?? 0)}
                subtitle={`${formatNumber(dashboard?.qtd_anuncio_cotacao ?? 0)} anúncios`}
                icon={<RequestQuote />}
                accentColor="#8b5cf6"
                bgColor="#f5f3ff"
                delay={420}
              />
              <KpiCard
                label="Combo"
                value={formatCurrency(dashboard?.valor_recebido_combo ?? 0)}
                subtitle={`${formatNumber(dashboard?.qtd_anuncio_combo ?? 0)} anúncios`}
                icon={<AttachMoney />}
                accentColor="#10b981"
                bgColor="#ecfdf5"
                delay={480}
              />
            </>
          )}
        </Box>

        {/* KPI Total Receita */}
        <Box display="grid" gridTemplateColumns="1fr" gap={2.5}>
          {loading ? (
            <KpiCardSkeleton delay={540} />
          ) : (
            <KpiCard
              label="Total Receita"
              value={formatCurrency(totalRecebido)}
              subtitle={`${formatNumber(
                (dashboard?.qtd_anuncio_normais ?? 0) +
                (dashboard?.qtd_anuncio_leilao ?? 0) +
                (dashboard?.qtd_anuncio_cotacao ?? 0) +
                (dashboard?.qtd_anuncio_combo ?? 0)
              )} anúncios no total`}
              icon={<Paid />}
              accentColor="#1e293b"
              bgColor="#f1f5f9"
              delay={540}
            />
          )}
        </Box>

        {/* Gráfico Mensal + Doughnut por Tipo */}
        <Box display="grid" gridTemplateColumns={{ xs: "1fr", lg: "2fr 1fr" }} gap={3}>
          <AnimatedBox $delay={600}>
            {loading ? (
              <ChartSkeleton />
            ) : (
              <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: "1px solid #f1f5f9", height: "100%" }}>
                <Typography fontWeight={800} fontSize="1rem" color="#1e293b" mb={0.5}>
                  Evolução Mensal
                </Typography>
                <Typography fontSize="0.8rem" color="#94a3b8" fontWeight={500} mb={3}>
                  Valor cobrado, recebido e quantidade vendida por mês
                </Typography>
                <Box height={340}>
                  <Bar data={monthlyChartData} options={monthlyChartOptions} />
                </Box>
              </Paper>
            )}
          </AnimatedBox>

          <AnimatedBox $delay={680}>
            {loading ? (
              <ChartSkeleton />
            ) : (
              <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: "1px solid #f1f5f9", height: "100%" }}>
                <Typography fontWeight={800} fontSize="1rem" color="#1e293b" mb={0.5}>
                  Receita por Tipo
                </Typography>
                <Typography fontSize="0.8rem" color="#94a3b8" fontWeight={500} mb={3}>
                  Distribuição de receita entre modalidades
                </Typography>
                <Box height={320} display="flex" alignItems="center" justifyContent="center">
                  <Doughnut data={receitaPorTipoData} options={doughnutOptions} />
                </Box>
              </Paper>
            )}
          </AnimatedBox>
        </Box>

        {/* TOP LOJISTAS RECEITA */}
        <AnimatedBox $delay={760}>
          {loading ? (
            <ChartSkeleton />
          ) : (
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: "1px solid #f1f5f9" }}>
              <Typography fontWeight={800} fontSize="1rem" color="#1e293b" mb={0.5}>
                Top Lojistas por Receita
              </Typography>
              <Typography fontSize="0.8rem" color="#94a3b8" fontWeight={500} mb={3}>
                Receita taxa canal e quantidade comprada por lojista
              </Typography>
              <Box height={topLojistasChartHeight}>
                <Bar data={topLojistasChartData} options={horizontalBarOptions("y")} />
              </Box>
            </Paper>
          )}
        </AnimatedBox>

        {/* TOP LOJISTAS TABLE */}
        <AnimatedBox $delay={840}>
          {loading ? (
            <TableSkeleton />
          ) : (
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: "1px solid #f1f5f9" }}>
              <Typography fontWeight={800} fontSize="1rem" color="#1e293b" mb={0.5}>
                Detalhamento Lojistas
              </Typography>
              <Typography fontSize="0.8rem" color="#94a3b8" fontWeight={500} mb={3}>
                Ranking de lojistas por receita gerada
              </Typography>
              <TableContainer sx={{ maxHeight: 480 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow sx={{ "& th": { bgcolor: "#f8fafc" } }}>
                      <TableCell sx={{ ...thSx, width: 40 }}>#</TableCell>
                      <TableCell sx={thSx}>Lojista</TableCell>
                      <TableCell sx={{ ...thSx, textAlign: "center" }}>Qtd Comprados</TableCell>
                      <TableCell sx={{ ...thSx, textAlign: "right" }}>Receita Taxa Canal</TableCell>
                      <TableCell sx={{ ...thSx, minWidth: 160 }}>Proporção</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(() => {
                      const totalVal = topLojistas.reduce((sum, r) => sum + r.valor_recebido_taxa_canal, 0) || 1;
                      return topLojistas.map((row, idx) => (
                        <TableRow key={row.num_id_revenda} sx={{ "&:hover": { bgcolor: "#f8fafc" } }}>
                          <TableCell sx={{ py: 2, fontSize: "0.8rem", fontWeight: 700, color: "#94a3b8", borderBottom: "1px solid #f8fafc" }}>
                            {idx + 1}
                          </TableCell>
                          <TableCell sx={{ py: 2, fontSize: "0.85rem", fontWeight: 700, color: "#1e293b", borderBottom: "1px solid #f8fafc" }}>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Storefront sx={{ fontSize: 16, color: "#94a3b8" }} />
                              {row.revenda_label}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ py: 2, fontSize: "0.85rem", fontWeight: 800, color: "#0284c7", textAlign: "center", borderBottom: "1px solid #f8fafc" }}>
                            {formatNumber(row.qtd_anuncio_comprado)}
                          </TableCell>
                          <TableCell sx={{ py: 2, fontSize: "0.85rem", fontWeight: 800, color: colorPrimary, textAlign: "right", borderBottom: "1px solid #f8fafc" }}>
                            {formatCurrency(row.valor_recebido_taxa_canal)}
                          </TableCell>
                          <TableCell sx={{ py: 2, borderBottom: "1px solid #f8fafc" }}>
                            <Box display="flex" alignItems="center" gap={1.5}>
                              <Box flex={1}>
                                <LinearProgress
                                  variant="determinate"
                                  value={(row.valor_recebido_taxa_canal / totalVal) * 100}
                                  sx={{
                                    height: 8,
                                    borderRadius: 4,
                                    bgcolor: "#f1f5f9",
                                    "& .MuiLinearProgress-bar": { borderRadius: 4, bgcolor: colorPrimary },
                                  }}
                                />
                              </Box>
                              <Typography fontSize="0.7rem" color="#94a3b8" fontWeight={600} minWidth={32}>
                                {Math.round((row.valor_recebido_taxa_canal / totalVal) * 100)}%
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ));
                    })()}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </AnimatedBox>

        {/* TOP ANUNCIANTES RECEITA */}
        <AnimatedBox $delay={920}>
          {loading ? (
            <ChartSkeleton />
          ) : (
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: "1px solid #f1f5f9" }}>
              <Typography fontWeight={800} fontSize="1rem" color="#1e293b" mb={0.5}>
                Top Anunciantes por Receita
              </Typography>
              <Typography fontSize="0.8rem" color="#94a3b8" fontWeight={500} mb={3}>
                Receita taxa canal e quantidade vendida por concessionária
              </Typography>
              <Box height={topAnunciantesChartHeight}>
                <Bar data={topAnunciantesChartData} options={horizontalBarOptions("y")} />
              </Box>
            </Paper>
          )}
        </AnimatedBox>

        {/* TOP ANUNCIANTES TABLE */}
        <AnimatedBox $delay={1000}>
          {loading ? (
            <TableSkeleton />
          ) : (
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: "1px solid #f1f5f9" }}>
              <Typography fontWeight={800} fontSize="1rem" color="#1e293b" mb={0.5}>
                Detalhamento Anunciantes
              </Typography>
              <Typography fontSize="0.8rem" color="#94a3b8" fontWeight={500} mb={3}>
                Ranking de concessionárias por receita gerada
              </Typography>
              <TableContainer sx={{ maxHeight: 480 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow sx={{ "& th": { bgcolor: "#f8fafc" } }}>
                      <TableCell sx={{ ...thSx, width: 40 }}>#</TableCell>
                      <TableCell sx={thSx}>Concessionária</TableCell>
                      <TableCell sx={{ ...thSx, textAlign: "center" }}>Qtd Vendidos</TableCell>
                      <TableCell sx={{ ...thSx, textAlign: "right" }}>Receita Taxa Canal</TableCell>
                      <TableCell sx={{ ...thSx, minWidth: 160 }}>Proporção</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(() => {
                      const totalVal = topAnunciantes.reduce((sum, r) => sum + r.valor_recebido_taxa_canal, 0) || 1;
                      return topAnunciantes.map((row, idx) => (
                        <TableRow key={row.num_id_concessionaria} sx={{ "&:hover": { bgcolor: "#f8fafc" } }}>
                          <TableCell sx={{ py: 2, fontSize: "0.8rem", fontWeight: 700, color: "#94a3b8", borderBottom: "1px solid #f8fafc" }}>
                            {idx + 1}
                          </TableCell>
                          <TableCell sx={{ py: 2, fontSize: "0.85rem", fontWeight: 700, color: "#1e293b", borderBottom: "1px solid #f8fafc" }}>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Store sx={{ fontSize: 16, color: "#94a3b8" }} />
                              {row.concessionaria_label}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ py: 2, fontSize: "0.85rem", fontWeight: 800, color: "#10b981", textAlign: "center", borderBottom: "1px solid #f8fafc" }}>
                            {formatNumber(row.qtd_anuncio_vendido)}
                          </TableCell>
                          <TableCell sx={{ py: 2, fontSize: "0.85rem", fontWeight: 800, color: "#8b5cf6", textAlign: "right", borderBottom: "1px solid #f8fafc" }}>
                            {formatCurrency(row.valor_recebido_taxa_canal)}
                          </TableCell>
                          <TableCell sx={{ py: 2, borderBottom: "1px solid #f8fafc" }}>
                            <Box display="flex" alignItems="center" gap={1.5}>
                              <Box flex={1}>
                                <LinearProgress
                                  variant="determinate"
                                  value={(row.valor_recebido_taxa_canal / totalVal) * 100}
                                  sx={{
                                    height: 8,
                                    borderRadius: 4,
                                    bgcolor: "#f1f5f9",
                                    "& .MuiLinearProgress-bar": { borderRadius: 4, bgcolor: "#8b5cf6" },
                                  }}
                                />
                              </Box>
                              <Typography fontSize="0.7rem" color="#94a3b8" fontWeight={600} minWidth={32}>
                                {Math.round((row.valor_recebido_taxa_canal / totalVal) * 100)}%
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ));
                    })()}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </AnimatedBox>
      </Box>
    </Fade>
  );
}
